import { useState } from 'react'
import { Alert, Input, Segmented, Space, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PrinterOutlined, StopOutlined, SyncOutlined, UserOutlined } from '@ant-design/icons'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatMoneyValue } from '@/shared/lib/money'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import { CONTRACT_STATUSES, fetchContracts, type Contract, type ContractStatus } from '@/features/contract/contractApi'
import { ContractCancelModal, ContractRenewModal } from '@/features/contract/ContractDecisionModals'
import { ContractExpiryTag } from '@/features/contract/SubscriberContracts'
import { useContractPrinter } from '@/features/contract/useContractPrinter'

const PAGE_SIZE = 50

/** Shartnomalar: tugayotgan va muddati o'tganlarni uzaytirish yoki bekor qilish (ADR-051). */
export function ContractsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const canManage = can('subscriber.contract.manage')
  const printer = useContractPrinter()
  const [status, setStatus] = useState<ContractStatus>('EXPIRING')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [renewing, setRenewing] = useState<Contract | null>(null)
  const [cancelling, setCancelling] = useState<Contract | null>(null)

  const query = { status, search, page, size: PAGE_SIZE }
  const contracts = useQuery({
    queryKey: queryKeys.contracts.list(query),
    queryFn: ({ signal }) => fetchContracts(query, signal),
    placeholderData: keepPreviousData,
  })

  const money = (row: Contract, value: number | null | undefined) =>
    value == null ? '—' : formatMoneyValue({ amount: value, currency: row.currency })

  const columns: ColumnsType<Contract> = [
    { key: 'number', title: t('contract.fields.number'), width: 120, render: (_, row) => <Typography.Text code>{row.number}</Typography.Text> },
    {
      key: 'subscriber',
      title: t('contract.fields.subscriber'),
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Link to={`/subscribers/${row.subscriberId}?tab=contract`}>{row.subscriberLogin}</Link>
          {row.subscriberName ? <Typography.Text type="secondary">{row.subscriberName}</Typography.Text> : null}
        </Space>
      ),
    },
    { key: 'kind', title: t('contract.fields.kind'), width: 150, render: (_, row) => t(`contract.kind.${row.kind}`) },
    { key: 'period', title: t('contract.fields.period'), width: 210, render: (_, row) => `${row.startsOn} → ${row.endsOn}` },
    { key: 'state', title: t('contract.fields.state'), width: 150, render: (_, row) => <ContractExpiryTag contract={row} /> },
    {
      key: 'amount',
      title: t('contract.fields.depositOrRental'),
      width: 150,
      align: 'right',
      render: (_, row) =>
        row.kind === 'RENTAL' ? money(row, row.monthlyRental) : row.kind === 'GUARANTEE' ? money(row, row.depositAmount) : '—',
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'open',
              label: t('contract.openSubscriber'),
              icon: <UserOutlined />,
              onClick: () => navigate(`/subscribers/${row.subscriberId}?tab=contract`),
            },
            {
              key: 'print',
              label: t('contract.print.button'),
              icon: <PrinterOutlined />,
              hidden: row.state !== 'ACTIVE',
              onClick: () => printer.print(row),
            },
            {
              key: 'renew',
              label: t('contract.renew'),
              icon: <SyncOutlined />,
              hidden: !canManage || row.state !== 'ACTIVE',
              onClick: () => setRenewing(row),
            },
            {
              key: 'cancel',
              label: t('contract.cancel'),
              icon: <StopOutlined />,
              danger: true,
              divider: true,
              hidden: !canManage || row.state !== 'ACTIVE',
              onClick: () => setCancelling(row),
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader title={t('nav.contracts')} description={t('pageIntro.contracts')} />
      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented<ContractStatus>
          value={status}
          onChange={(value) => {
            setStatus(value)
            setPage(0)
          }}
          options={CONTRACT_STATUSES.map((value) => ({ value, label: t(`contract.status.${value}`) }))}
        />
        <Input.Search
          allowClear
          style={{ width: 280 }}
          placeholder={t('contract.searchPlaceholder')}
          onSearch={(value) => {
            setSearch(value)
            setPage(0)
          }}
        />
      </Space>
      {contracts.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(contracts.error, t)} />
      ) : null}
      <Table<Contract>
        rowKey={(row) => row.id}
        loading={contracts.isFetching}
        columns={columns}
        dataSource={contracts.data?.rows ?? []}
        scroll={{ x: 1000 }}
        pagination={{
          current: page + 1,
          pageSize: PAGE_SIZE,
          total: contracts.data?.total ?? 0,
          showSizeChanger: false,
          onChange: (next) => setPage(next - 1),
        }}
        locale={{ emptyText: t('contract.empty') }}
      />
      <ContractRenewModal contract={renewing} onClose={() => setRenewing(null)} />
      <ContractCancelModal contract={cancelling} onClose={() => setCancelling(null)} />
      {printer.portal}
    </>
  )
}
